import { validateUsername, validateEmail } from '@loumars/client-core/validation';

declare global {
  interface Window {
    afInitLivingElevationViewport?: () => void;
    afInitAuthFormSync?: () => void;
  }
}

export function initLivingElevationViewport(): void {
  const switcherButtons = document.querySelectorAll<HTMLButtonElement>('[data-blueprint-switcher]');
  if (switcherButtons.length === 0) return;

  switcherButtons.forEach(function (btn) {
    if (btn.dataset.bound === 'true') return;
    btn.dataset.bound = 'true';
    btn.addEventListener('click', function () {
      const mode = btn.getAttribute('data-blueprint-switcher');
      if (!mode) return;

      const container =
        btn.closest('#elevation-mode-switcher, [data-blueprint-switcher-container]') ||
        btn.parentElement;
      if (container) {
        const siblingBtns = container.querySelectorAll<HTMLButtonElement>(
          '[data-blueprint-switcher]'
        );
        siblingBtns.forEach(function (sibling) {
          sibling.classList.remove('bg-[#D90429]', 'text-white', 'font-semibold', 'shadow-xs');
          sibling.classList.add('text-slate-400');
        });
      }

      btn.classList.add('bg-[#D90429]', 'text-white', 'font-semibold', 'shadow-xs');
      btn.classList.remove('text-slate-400');

      const widget =
        btn.closest('[data-widget-type="Services"]') ||
        document.getElementById('blueprint-display-zone')?.parentElement ||
        document;
      const displayZone = widget.querySelector<HTMLElement>('#blueprint-display-zone');
      if (displayZone) {
        displayZone.setAttribute('data-blueprint-mode', mode);

        const conduits = displayZone.querySelectorAll<SVGPathElement>('.cad-conduit-pulse');
        const hudCards = displayZone.querySelectorAll<HTMLElement>('.border-\\[\\#26262B\\]');

        if (mode === 'conduit') {
          conduits.forEach(function (c) {
            c.setAttribute('stroke-width', '4');
          });
        } else {
          conduits.forEach(function (c) {
            c.setAttribute('stroke-width', '3');
          });
        }

        if (mode === 'metrics') {
          hudCards.forEach(function (card) {
            card.classList.add('border-red-900/50');
          });
        } else {
          hudCards.forEach(function (card) {
            card.classList.remove('border-red-900/50');
          });
        }
      }
    });
  });
}

export function initAuthFormSync(): void {
  if (typeof document === 'undefined') return;

  const authForms = document.querySelectorAll<HTMLFormElement>(
    'form[data-auth-form], form[action*="Login"], form[action*="Register"], form[action*="ForgotPassword"], form[action*="ResetPassword"]'
  );

  authForms.forEach(function (form) {
    function syncFields(): void {
      const isRegister = form.dataset.authForm === 'register' || form.action.includes('Register');

      // Dedicated Username inputs (NEVER sync email here)
      const userInputs = form.querySelectorAll<HTMLInputElement>(
        'input[name="UserName"], input[name="Username"]'
      );
      userInputs.forEach(function (inp) {
        const val = inp.value;
        if (!val) return;
        const selector = isRegister
          ? 'input[name*="registerUserForm.UserName"], input[name*="RegisterUserForm.UserName"], input[name*="registerForm.UserName"], input[name*="RegisterForm.UserName"]'
          : 'input[name*="loginForm.UserName"], input[name*="LoginForm.UserName"]';
        const fallbacks = form.querySelectorAll<HTMLInputElement>(selector);
        fallbacks.forEach(function (fb) {
          fb.value = val;
        });
      });

      // Dedicated Email inputs (NEVER sync username here)
      const emailInputs = form.querySelectorAll<HTMLInputElement>(
        'input[name="Email"], input[type="email"]'
      );
      emailInputs.forEach(function (inp) {
        const val = inp.value;
        if (!val) return;
        const selector = isRegister
          ? 'input[name*="registerUserForm.Email"], input[name*="RegisterUserForm.Email"], input[name*="registerForm.Email"], input[name*="RegisterForm.Email"]'
          : 'input[name*="loginForm.UserName"], input[name*="LoginForm.UserName"], input[name*="forgotPasswordForm.UsernameOrEmail"], input[name*="ForgotPasswordForm.UsernameOrEmail"], input[name*="resetPasswordForm.UsernameOrEmail"], input[name*="ResetPasswordForm.UsernameOrEmail"]';
        const fallbacks = form.querySelectorAll<HTMLInputElement>(selector);
        fallbacks.forEach(function (fb) {
          fb.value = val;
        });
      });

      // UsernameOrEmail inputs (Login / Forgot Password)
      const userOrEmailInputs = form.querySelectorAll<HTMLInputElement>(
        'input[name="UsernameOrEmail"]'
      );
      userOrEmailInputs.forEach(function (inp) {
        const val = inp.value;
        if (!val) return;
        const fallbacks = form.querySelectorAll<HTMLInputElement>(
          'input[name*="loginForm.UserName"], input[name*="LoginForm.UserName"], input[name*="forgotPasswordForm.UsernameOrEmail"], input[name*="ForgotPasswordForm.UsernameOrEmail"], input[name*="resetPasswordForm.UsernameOrEmail"], input[name*="ResetPasswordForm.UsernameOrEmail"]'
        );
        fallbacks.forEach(function (fb) {
          fb.value = val;
        });
      });

      const pwdInputs = form.querySelectorAll<HTMLInputElement>(
        'input[name="Password"], input[name="NewPassword"]'
      );
      pwdInputs.forEach(function (inp) {
        const val = inp.value;
        if (!val) return;
        const fallbacks = form.querySelectorAll<HTMLInputElement>(
          'input[name*="loginForm.Password"], input[name*="LoginForm.Password"], input[name*="registerUserForm.Password"], input[name*="RegisterUserForm.Password"], input[name*="resetPasswordForm.NewPassword"], input[name*="ResetPasswordForm.NewPassword"]'
        );
        fallbacks.forEach(function (fb) {
          fb.value = val;
        });
      });

      const confirmPwdInputs = form.querySelectorAll<HTMLInputElement>(
        'input[name="ConfirmPassword"]'
      );
      confirmPwdInputs.forEach(function (inp) {
        const val = inp.value;
        if (!val) return;
        const fallbacks = form.querySelectorAll<HTMLInputElement>(
          'input[name*="registerUserForm.ConfirmPassword"], input[name*="RegisterUserForm.ConfirmPassword"], input[name*="resetPasswordForm.ConfirmPassword"], input[name*="ResetPasswordForm.ConfirmPassword"]'
        );
        fallbacks.forEach(function (fb) {
          fb.value = val;
        });
      });
    }

    form.addEventListener('input', syncFields);

    // Real-time and on-blur validation for registration forms
    const isRegisterForm = form.dataset.authForm === 'register' || form.action.includes('Register');

    if (isRegisterForm) {
      const usernameInput = form.querySelector<HTMLInputElement>(
        'input[name="UserName"], input[name="Username"]'
      );
      const emailInput = form.querySelector<HTMLInputElement>(
        'input[name="Email"], input[type="email"]'
      );

      function getOrCreateErrorEl(input: HTMLInputElement, idSuffix: string): HTMLElement {
        let errorEl = form.querySelector<HTMLElement>(`#${idSuffix}`);
        if (!errorEl) {
          errorEl = document.createElement('div');
          errorEl.id = idSuffix;
          errorEl.className = 'text-rose-400 text-xs font-mono mt-1 hidden';
          errorEl.setAttribute('role', 'alert');
          input.parentNode?.appendChild(errorEl);
        }
        return errorEl;
      }

      function validateUsernameInput(): boolean {
        if (!usernameInput) return true;
        const errorEl = getOrCreateErrorEl(usernameInput, 'acms-reg-user-error');
        const val = usernameInput.value.trim();
        if (!val) {
          errorEl.textContent = 'Username is required.';
          errorEl.classList.remove('hidden');
          usernameInput.classList.add('border-rose-500', 'focus:border-rose-500');
          return false;
        }
        const res = validateUsername(val);
        if (!res.isValid) {
          errorEl.textContent = res.error || 'Invalid username.';
          errorEl.classList.remove('hidden');
          usernameInput.classList.add('border-rose-500', 'focus:border-rose-500');
          return false;
        }
        errorEl.textContent = '';
        errorEl.classList.add('hidden');
        usernameInput.classList.remove('border-rose-500', 'focus:border-rose-500');
        return true;
      }

      function validateEmailInput(): boolean {
        if (!emailInput) return true;
        const errorEl = getOrCreateErrorEl(emailInput, 'acms-reg-email-error');
        const val = emailInput.value.trim();
        if (!val) {
          errorEl.textContent = 'Email address is required.';
          errorEl.classList.remove('hidden');
          emailInput.classList.add('border-rose-500', 'focus:border-rose-500');
          return false;
        }
        const res = validateEmail(val);
        if (!res.isValid) {
          errorEl.textContent = res.error || 'Please enter a valid email address.';
          errorEl.classList.remove('hidden');
          emailInput.classList.add('border-rose-500', 'focus:border-rose-500');
          return false;
        }
        errorEl.textContent = '';
        errorEl.classList.add('hidden');
        emailInput.classList.remove('border-rose-500', 'focus:border-rose-500');
        return true;
      }

      if (usernameInput) {
        usernameInput.addEventListener('blur', validateUsernameInput);
        usernameInput.addEventListener('input', function () {
          const errorEl = form.querySelector<HTMLElement>('#acms-reg-user-error');
          if (errorEl && !errorEl.classList.contains('hidden')) {
            validateUsernameInput();
          }
        });
      }

      if (emailInput) {
        emailInput.addEventListener('blur', validateEmailInput);
        emailInput.addEventListener('input', function () {
          const errorEl = form.querySelector<HTMLElement>('#acms-reg-email-error');
          if (errorEl && !errorEl.classList.contains('hidden')) {
            validateEmailInput();
          }
        });
      }

      form.addEventListener('submit', function (e) {
        syncFields();
        const userValid = validateUsernameInput();
        const emailValid = validateEmailInput();

        if (!userValid || !emailValid) {
          e.preventDefault();
          e.stopPropagation();
          if (!userValid && usernameInput) {
            usernameInput.focus();
          } else if (!emailValid && emailInput) {
            emailInput.focus();
          }
        }
      });
    } else {
      form.addEventListener('submit', syncFields);
    }
  });
}

if (typeof window !== 'undefined') {
  window.afInitLivingElevationViewport = initLivingElevationViewport;
  window.afInitAuthFormSync = initAuthFormSync;
}
